import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useCart } from "./CartContext";
import { createOrder } from "../utils/api";


// Currencies PayPal can actually process. Mirrors PAYPAL_SUPPORTED_CURRENCIES
// in backend/payments/paypal.js — anything else is charged in USD instead.
const PAYPAL_NATIVE_CURRENCIES = new Set([
  "AUD", "BRL", "CAD", "CNY", "CZK", "DKK", "EUR", "HKD", "HUF", "ILS",
  "JPY", "MYR", "MXN", "TWD", "NZD", "NOK", "PHP", "PLN", "GBP", "RUB",
  "SGD", "SEK", "CHF", "THB", "USD",
]);

function generateOrderKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `key_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { cartItems, clearCart, currencySymbol, currency } = useCart();

  const [isPlacing, setIsPlacing] = useState(false);

  // One key per checkout attempt. Reusing it means a double-click or a retry
  // returns the SAME order instead of creating a duplicate — and, for COD, a
  // duplicate Leopard booking.
  const [orderKey, setOrderKey] = useState(generateOrderKey);

  // Show a message if the customer cancelled payment at the gateway.
  useEffect(() => {
    if (searchParams.get("status") === "cancelled") {
      toast.error("Payment was cancelled. You can try again below.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    paymentMethod: "cod", 
    specialInstructions: "",
  });

  const isPKR = currency === "PKR";

  // ✅ Available payment methods.
  //  - safepay : Card / JazzCash / Easypaisa (Safepay shows all of these)
  //  - paypal  : PayPal + international card (best for foreign/Saudi customers)
  //  - cod     : Cash on Delivery (Pakistan / PKR only)
  const paymentOptions = useMemo(() => {
    const opts = [
      {
        id: "safepay",
        label: "Card / JazzCash / Easypaisa",
        desc: "Pay securely online via Safepay",
      },
      {
        id: "paypal",
        label: "PayPal",
        desc: PAYPAL_NATIVE_CURRENCIES.has(currency)
          ? "Pay with your PayPal balance or an international card"
          : `PayPal can't process ${currency} — you'll be charged the USD price`,
      },
    ];
    if (isPKR) {
      opts.push({
        id: "cod",
        label: "Cash on Delivery",
        desc: "Pay in cash when your order is delivered (Pakistan only)",
      });
    }
    return opts;
  }, [isPKR, currency]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ✅ fixed unit price
  const getUnitPrice = (item) => Number(item?.prices?.[currency]) || 0;

  // ✅ total in selected currency
  const totalDisplay = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const unit = getUnitPrice(item);
      const qty = Number(item.quantity) || 1;
      return sum + unit * qty;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, currency]);

  // Keep the selected payment method valid when currency (and thus options) change.
  useEffect(() => {
    const validIds = paymentOptions.map((o) => o.id);
    if (!validIds.includes(formData.paymentMethod)) {
      setFormData((p) => ({ ...p, paymentMethod: validIds[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentOptions]);

  const handlePlaceOrder = async () => {
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.phone.trim() ||
      !formData.address.trim() ||
      !formData.city.trim()
    ) {
      toast.error(
        "Please fill all required fields (Name, Email, Phone, Address, City)."
      );
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      toast.error("Please enter a valid email — your order confirmation goes there.");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }

    const validIds = paymentOptions.map((o) => o.id);
    if (!validIds.includes(formData.paymentMethod)) {
      toast.error("Please select a valid payment method.");
      return;
    }

    if (formData.paymentMethod === "cod" && currency !== "PKR") {
      toast.error("COD is only available for PKR (Pakistan).");
      return;
    }

    const missingPrice = cartItems.find((i) => !getUnitPrice(i));
    if (missingPrice) {
      toast.error(`Price not available in ${currency} for: ${missingPrice.title}`);
      return;
    }

    try {
      setIsPlacing(true);


      const items = cartItems.map((item) => ({
        productId: item.id,
        quantity: Number(item.quantity) || 1,
      }));

      const payload = {
        clientOrderKey: orderKey,
        currency,
        customer: {
          name: formData.name.trim(),
          email: formData.email.trim() || "",
          phone: formData.phone.trim(),
          address: formData.address.trim(),
          city: formData.city.trim(),
          postalCode: formData.postalCode.trim() || "",
          specialInstructions: formData.specialInstructions.trim() || "",
        },
        items,
        paymentMethod: formData.paymentMethod,
      };

      const res = await createOrder(payload);

      // Online gateways (Safepay / PayPal) return a URL to redirect the customer
      // to the hosted payment page. When that's present, send them there.
      if (res.checkoutUrl) {
        // Don't clear the cart yet — if the customer cancels at the gateway
        // they come back to a cart that still has their books in it.
        // OrderSuccess clears it once the payment actually goes through.
        window.location.href = res.checkoutUrl;
        return;
      }

      if (res.status === "pending_payment") {
        toast.error(
          "We couldn't open the payment page. Please try again, or choose another payment method."
        );
        return;
      }

      if (res.status === "booked") {
        toast.success(`Order placed! Tracking number: ${res.trackingNumber}`);
      } else {
        toast.success(`Order created! ID: ${res.orderId}`);
      }

      clearCart();
      setOrderKey(generateOrderKey());
      navigate("/");
    } catch (e) {
      toast.error(e?.message || "Order failed");
    } finally {
      setIsPlacing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-2 gap-8">
      {/* Shipping Form */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">Shipping Details</h2>

        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <input
            type="text"
            name="name"
            placeholder="Full Name *"
            value={formData.name}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            required
          />

          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            required
          />

          <input
            type="tel"
            name="phone"
            placeholder="Phone Number *"
            value={formData.phone}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            required
          />

          <input
            type="text"
            name="address"
            placeholder="Street Address *"
            value={formData.address}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            required
          />

          <input
            type="text"
            name="city"
            placeholder="City * (e.g., Lahore)"
            value={formData.city}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            required
          />

          <input
            type="text"
            name="postalCode"
            placeholder="Postal Code"
            value={formData.postalCode}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />

          <textarea
            name="specialInstructions"
            placeholder="Special instructions (optional)"
            value={formData.specialInstructions}
            onChange={handleChange}
            className="w-full border p-2 rounded min-h-[90px]"
          />

          <div className="mt-6">
            <h3 className="font-semibold mb-3">Payment Method</h3>

            <div className="space-y-2">
              {paymentOptions.map((opt) => {
                const selected = formData.paymentMethod === opt.id;
                return (
                  <label
                    key={opt.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selected
                        ? "border-brand bg-brand/10 ring-1 ring-brand"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={opt.id}
                      checked={selected}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <span>
                      <span className="block font-medium text-gray-800">
                        {opt.label}
                      </span>
                      <span className="block text-sm text-gray-500">
                        {opt.desc}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={handlePlaceOrder}
            disabled={isPlacing}
            className="mt-4 w-full bg-brand text-black font-semibold py-3 rounded-xl hover:bg-black hover:text-brand transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isPlacing ? "Placing Order..." : "Place Order"}
          </button>
        </form>
      </div>

      {/* Cart Summary */}
      <div className="bg-gray-50 p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-semibold mb-4">Order Summary</h2>

        {cartItems.length === 0 ? (
          <p>Your cart is empty.</p>
        ) : (
          <>
            {cartItems.map((item) => {
              const unitDisplay = getUnitPrice(item);
              const qty = Number(item.quantity) || 1;
              const line = unitDisplay * qty;

              return (
                <div key={item.id} className="flex justify-between mb-3">
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-gray-500">
                      {qty} × {currencySymbol}
                      {unitDisplay.toFixed(2)}
                    </p>
                  </div>
                  <p className="font-semibold">
                    {currencySymbol}
                    {line.toFixed(2)}
                  </p>
                </div>
              );
            })}

            <hr className="my-4" />

            <p className="flex justify-between text-lg font-semibold">
              <span>Total:</span>
              <span>
                {currencySymbol}
                {totalDisplay.toFixed(2)}
              </span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
